/**
 * Wiederverwendbarer XLSX-Import-Dialog mit 2-Schritt-Ablauf:
 * Datei wählen → Vorschau (je Zeile Neu/Update/Übersprungen/Fehler) → Bestätigen.
 *
 * Erwartet Backend-Endpunkte, die folgende Form liefern:
 *  - preview: { rows: [{row, action, label, detail, match_name, messages[]}], summary, total_rows }
 *  - commit:  { created, updated, skipped, errors[], warnings[], total_rows }
 */
import React, { useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Upload, Download, CheckCircle2, AlertTriangle, FileSpreadsheet, RotateCcw } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const ACTION_BADGE = {
  create: <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">Neu</Badge>,
  update: <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 text-[10px]">Update</Badge>,
  skip: <Badge variant="outline" className="text-[10px]">Übersprungen</Badge>,
  error: <Badge variant="destructive" className="text-[10px]">Fehler</Badge>,
};

export const XlsxImportDialog = ({
  open, onOpenChange, title, description, infoLines = [],
  templateUrl, previewUrl, commitUrl, templateFilename = 'Vorlage.xlsx', onImported,
  enableFieldSelection = false, enableCreateNew = false, undoUrlBase = null,
}) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedFields, setSelectedFields] = useState(null);
  const [createNew, setCreateNew] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undone, setUndone] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setResult(null); setSelectedFields(null); setUndone(false); };
  const close = (o) => { if (committing || previewing) return; onOpenChange(o); if (!o) reset(); };

  const toggleField = (field) => {
    setSelectedFields((prev) => {
      const cur = prev || [];
      return cur.includes(field) ? cur.filter((f) => f !== field) : [...cur, field];
    });
  };

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await api.get(templateUrl, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = templateFilename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Vorlage konnte nicht geladen werden'); }
    finally { setDownloading(false); }
  };

  const runPreview = async (f, createNewFlag) => {
    if (!f) return;
    setResult(null); setPreviewing(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      if (enableCreateNew) fd.append('create_new', createNewFlag ? 'true' : 'false');
      const res = await api.post(previewUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      if (enableFieldSelection) {
        setSelectedFields((res.data.detected_fields || []).map((x) => x.field));
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Vorschau fehlgeschlagen'); setFile(null); }
    finally { setPreviewing(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!f) return;
    setFile(f); setPreview(null);
    await runPreview(f, createNew);
  };

  const onToggleCreateNew = async (checked) => {
    setCreateNew(checked);
    if (file) await runPreview(file, checked);
  };

  const commit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      if (enableFieldSelection && selectedFields) fd.append('fields', JSON.stringify(selectedFields));
      if (enableCreateNew) fd.append('create_new', createNew ? 'true' : 'false');
      const res = await api.post(commitUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data); setPreview(null); setFile(null); setUndone(false);
      const total = (res.data.created || 0) + (res.data.updated || 0);
      if (total > 0) { toast.success(`${res.data.created || 0} angelegt, ${res.data.updated || 0} aktualisiert`); onImported?.(); }
      else toast.info('Keine Datensätze importiert');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import fehlgeschlagen'); }
    finally { setCommitting(false); }
  };

  const undoImport = async () => {
    if (!result?.batch_id || !undoUrlBase) return;
    setUndoing(true);
    try {
      const res = await api.post(`${undoUrlBase}/${result.batch_id}/undo`);
      setUndone(true);
      toast.success(`Rückgängig: ${res.data.restored_updates || 0} wiederhergestellt, ${res.data.deleted_created || 0} gelöscht`);
      onImported?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Rückgängig fehlgeschlagen'); }
    finally { setUndoing(false); }
  };

  const s = preview?.summary || {};
  const applicable = (s.create || 0) + (s.update || 0);
  const detectedFields = preview?.detected_fields || [];
  const noFieldsSelected = enableFieldSelection && (selectedFields?.length || 0) === 0;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="xlsx-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" /> {title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!preview && !result && (
            <>
              {infoLines.length > 0 && (
                <div className="rounded-md border border-border p-3 bg-secondary/20 text-xs text-muted-foreground space-y-1">
                  {infoLines.map((l, i) => (<p key={i}>• {l}</p>))}
                </div>
              )}
              <Button variant="outline" className="w-full rounded-full" onClick={downloadTemplate} disabled={downloading} data-testid="import-download-template-btn">
                {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />} Vorlage herunterladen
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onFile} data-testid="xlsx-import-file-input" />
              {enableCreateNew && (
                <label className="flex items-center gap-2.5 rounded-md border border-border p-3 text-sm cursor-pointer" data-testid="import-create-new-toggle">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border"
                    checked={createNew}
                    onChange={(e) => onToggleCreateNew(e.target.checked)}
                  />
                  <span>
                    <span className="font-medium">Neue Mitarbeiter anlegen</span>
                    <span className="block text-xs text-muted-foreground">Nicht gefundene Namen werden neu angelegt (ohne Login-Zugang).</span>
                  </span>
                </label>
              )}
              <Button className="w-full rounded-full" onClick={() => fileRef.current?.click()} disabled={previewing} data-testid="import-choose-file-btn">
                {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />} Excel-Datei auswählen
              </Button>
            </>
          )}

          {preview && (
            <div className="space-y-3" data-testid="import-preview">
              {enableCreateNew && (
                <label className="flex items-center gap-2.5 rounded-md border border-border p-2.5 text-sm cursor-pointer" data-testid="import-create-new-toggle-preview">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border"
                    checked={createNew}
                    disabled={previewing}
                    onChange={(e) => onToggleCreateNew(e.target.checked)}
                  />
                  <span className="font-medium">Neue Mitarbeiter anlegen</span>
                  {previewing && <Loader2 className="h-3.5 w-3.5 animate-spin ml-1" />}
                </label>
              )}
              <div className="flex flex-wrap gap-2 text-sm items-center">
                {s.create > 0 && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{s.create} neu</Badge>}
                {s.update > 0 && <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100">{s.update} aktualisieren</Badge>}
                {s.skip > 0 && <Badge variant="outline">{s.skip} übersprungen</Badge>}
                {s.error > 0 && <Badge variant="destructive">{s.error} Fehler</Badge>}
                <span className="text-muted-foreground ml-auto">{preview.total_rows} Zeile(n)</span>
              </div>

              {enableFieldSelection && detectedFields.length > 0 && (
                <div className="rounded-md border border-border p-3 bg-secondary/20 space-y-2" data-testid="import-field-selection">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold">Welche Daten sollen übernommen werden?</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-[11px] text-primary hover:underline"
                        onClick={() => setSelectedFields(detectedFields.map((f) => f.field))}
                        data-testid="import-fields-select-all"
                      >Alle</button>
                      <button
                        type="button"
                        className="text-[11px] text-muted-foreground hover:underline"
                        onClick={() => setSelectedFields([])}
                        data-testid="import-fields-select-none"
                      >Keine</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
                    {detectedFields.map((f) => (
                      <label key={f.field} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`import-field-${f.field}`}>
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 rounded border-border"
                          checked={(selectedFields || []).includes(f.field)}
                          onChange={() => toggleField(f.field)}
                        />
                        <span>{f.label}</span>
                      </label>
                    ))}
                  </div>
                  {noFieldsSelected && (
                    <p className="text-[11px] text-amber-700">Bitte mindestens ein Feld auswählen.</p>
                  )}
                </div>
              )}

              <div className="rounded-md border border-border overflow-auto max-h-[45vh]">
                <table className="w-full text-xs border-collapse">
                  <thead className="sticky top-0 bg-secondary/80 backdrop-blur">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium border-b border-border">Aktion</th>
                      <th className="px-2 py-1.5 text-left font-medium border-b border-border">Bezeichnung</th>
                      <th className="px-2 py-1.5 text-left font-medium border-b border-border">Detail</th>
                      <th className="px-2 py-1.5 text-left font-medium border-b border-border">Hinweise</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.row} className="odd:bg-secondary/20" data-testid={`import-preview-row-${r.row}`}>
                        <td className="px-2 py-1 border-b border-border/50">{ACTION_BADGE[r.action]}</td>
                        <td className="px-2 py-1 border-b border-border/50">
                          {r.label || '—'}
                          {r.action === 'update' && r.match_name && r.match_name !== r.label && (
                            <div className="text-[10px] text-muted-foreground">aus Datei: {r.match_name}</div>
                          )}
                        </td>
                        <td className="px-2 py-1 border-b border-border/50 text-muted-foreground">{r.detail || ''}</td>
                        <td className="px-2 py-1 border-b border-border/50 text-amber-700">
                          {(r.messages || []).join('; ')}
                          {r.changes && r.changes.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {r.changes.map((c) => (
                                <span
                                  key={`${r.row}-${c.field}`}
                                  className="inline-flex items-center gap-1 rounded bg-sky-50 px-1.5 py-0.5 text-[10px] text-sky-700 border border-sky-200/70"
                                >
                                  <span className="font-medium">{c.label}:</span>
                                  <span className="line-through opacity-70">{c.old}</span>
                                  <span>→</span>
                                  <span className="font-semibold">{c.new}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={reset} disabled={committing}>Andere Datei</Button>
                <Button onClick={commit} disabled={committing || applicable === 0 || noFieldsSelected} data-testid="import-confirm-btn">
                  {committing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Import bestätigen ({applicable})
                </Button>
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-md border border-border p-3 space-y-2" data-testid="import-result">
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                <CheckCircle2 className="h-4 w-4" /> {result.created || 0} angelegt · {result.updated || 0} aktualisiert
                {result.skipped > 0 && <span className="text-muted-foreground">· {result.skipped} übersprungen</span>}
              </div>
              {result.errors?.length > 0 && (
                <div className="text-xs text-destructive space-y-0.5">
                  <div className="flex items-center gap-1 font-medium"><AlertTriangle className="h-3.5 w-3.5" /> Fehler ({result.errors.length}):</div>
                  {result.errors.slice(0, 8).map((er, i) => (<div key={i}>Zeile {er.row}: {er.message}</div>))}
                </div>
              )}
              {result.warnings?.length > 0 && (
                <div className="text-xs text-amber-700 space-y-0.5">
                  <div className="font-medium">Hinweise ({result.warnings.length}):</div>
                  {result.warnings.slice(0, 8).map((w, i) => (<div key={i}>Zeile {w.row}: {w.message}</div>))}
                </div>
              )}
              <div className="flex flex-wrap justify-end gap-2 pt-1">
                {undoUrlBase && result.batch_id && !undone && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    onClick={undoImport}
                    disabled={undoing}
                    data-testid="import-undo-btn"
                  >
                    {undoing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
                    Letzten Import rückgängig machen
                  </Button>
                )}
                {undone && (
                  <span className="text-xs text-emerald-700 self-center" data-testid="import-undone-note">Import wurde rückgängig gemacht.</span>
                )}
                <Button variant="outline" size="sm" onClick={reset} data-testid="import-again-btn">Weitere Datei importieren</Button>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => close(false)} disabled={committing || previewing}>Schließen</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
